import { MAX_CHAT_NOTE_LENGTH } from "@loot-os/api-contract";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";

import {
  CHAT_MUTE_DURATIONS,
  type ChatMuteDuration,
  isChatMuteDuration,
  muteDurationSeconds,
  useInvalidateChat,
} from "../lib/chat";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Mutes an end user in every chat room of the environment, for a chosen time or until unmuted,
 * with a reason staff can read later. Editors and above; renders nothing below that.
 */
export function ChatMuteButton({
  endUserId,
  name,
  size = "sm",
  variant = "outline",
}: {
  endUserId: string;
  /** Who, in the dialog's title; the username when there is one. */
  name?: string | null;
  size?: "sm" | "xs";
  variant?: "outline" | "ghost";
}) {
  const trpc = useTRPC();
  const { can } = useTenantRole();
  const invalidate = useInvalidateChat();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState<ChatMuteDuration>("86400");
  const [reason, setReason] = useState("");

  const mute = useMutation(
    trpc.chat.mute.mutationOptions({
      onSuccess: () => {
        toast.success("Muted in chat.");
        setOpen(false);
        setReason("");
        void invalidate();
      },
    }),
  );

  if (!can("editor")) return null;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={<Button type="button" variant={variant} size={size} disabled={mute.isPending} />}
      >
        Mute
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mute {name ? `@${name}` : "this shopper"} in chat?</AlertDialogTitle>
          <AlertDialogDescription>
            They can still read the room, but every message they send is refused until the mute ends
            or someone unmutes them. A new mute replaces any mute already in place.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor={`${id}-duration`}>For</Label>
            <Select
              value={duration}
              items={CHAT_MUTE_DURATIONS}
              onValueChange={(value) => {
                if (isChatMuteDuration(value)) setDuration(value);
              }}
            >
              <SelectTrigger id={`${id}-duration`} className="w-full" aria-label="Mute duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CHAT_MUTE_DURATIONS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${id}-reason`}>Reason</Label>
            <Input
              id={`${id}-reason`}
              maxLength={MAX_CHAT_NOTE_LENGTH}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!reason.trim() || mute.isPending}
            onClick={(event) => {
              // The dialog closes on success, not on click, so a refused mute keeps the draft.
              event.preventDefault();
              mute.mutate({
                endUserId,
                reason: reason.trim(),
                durationSeconds: muteDurationSeconds(duration),
              });
            }}
          >
            Mute
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Lifts a chat mute at once. Editors and above; renders nothing below that. */
export function ChatUnmuteButton({ endUserId }: { endUserId: string }) {
  const trpc = useTRPC();
  const { can } = useTenantRole();
  const invalidate = useInvalidateChat();
  const unmute = useMutation(
    trpc.chat.unmute.mutationOptions({
      onSuccess: () => {
        toast.success("Unmuted.");
        void invalidate();
      },
    }),
  );
  if (!can("editor")) return null;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={unmute.isPending}
      onClick={() => unmute.mutate({ endUserId })}
    >
      Unmute
    </Button>
  );
}

/** Deletes one message for everyone, after a confirm. Editors and above. */
export function ChatDeleteMessageButton({
  roomId,
  messageId,
}: {
  roomId: "general";
  messageId: string;
}) {
  const trpc = useTRPC();
  const { can } = useTenantRole();
  const invalidate = useInvalidateChat();
  const remove = useMutation(
    trpc.chat.deleteMessage.mutationOptions({
      onSuccess: () => {
        toast.success("Message deleted.");
        void invalidate();
      },
    }),
  );
  if (!can("editor")) return null;
  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={<Button type="button" variant="ghost" size="sm" disabled={remove.isPending} />}
      >
        Delete
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this message?</AlertDialogTitle>
          <AlertDialogDescription>
            Its text disappears for everyone in the room and replies lose their quote. The sender is
            not told who deleted it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => remove.mutate({ roomId, messageId })}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
